import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  buildGooglePdfViewerUrl,
  encodeFilePreviewUrl,
  getUploadFilePreviewKind,
  UploadFilePreviewKind,
} from '../../helpers/upload-file-preview.util';

type ResizeEdge = 'left' | 'right' | 'bottom' | 'corner-bl' | 'corner-br';

interface PanelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-floating-upload-preview-panel',
  templateUrl: './floating-upload-preview-panel.component.html',
  styleUrls: ['./floating-upload-preview-panel.component.scss'],
})
export class FloatingUploadPreviewPanelComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  fileName = '';
  previewUrl = '';

  safePdfUrl: SafeResourceUrl | null = null;
  jsonPreview = '';
  isLoadingJson = false;
  isLoadingPdf = false;
  jsonLoadFailed = false;
  pdfLoadFailed = false;

  panelX = 0;
  panelY = 0;
  panelWidth = 0;
  panelHeight = 0;
  isMinimized = false;
  isMaximized = false;

  private readonly minWidth = 360;
  private readonly minHeight = 280;
  private readonly margin = 8;
  private pdfBlobUrl: string | null = null;
  private savedBounds: PanelBounds | null = null;
  private dragState: { startX: number; startY: number; originX: number; originY: number } | null =
    null;
  private resizeState: {
    edge: ResizeEdge;
    startX: number;
    startY: number;
    origin: PanelBounds;
  } | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  get kind(): UploadFilePreviewKind {
    return getUploadFilePreviewKind(this.fileName);
  }

  ngOnInit(): void {
    this.resetDefaultBounds();
    if (this.kind === 'pdf') {
      this.loadPdfPreview();
    } else if (this.kind === 'json') {
      this.loadJsonPreview();
    }
  }

  ngOnDestroy(): void {
    this.revokePdfBlobUrl();
    this.endInteraction();
  }

  get imagePreviewUrl(): string {
    return encodeFilePreviewUrl(this.previewUrl);
  }

  get panelStyle(): Record<string, string> {
    if (this.isMinimized) {
      return {
        left: `${this.panelX}px`,
        top: `${window.innerHeight - 52 - this.margin}px`,
        width: `${this.panelWidth}px`,
        height: '52px',
      };
    }
    if (this.isMaximized) {
      return {
        left: `${this.margin}px`,
        top: `${this.margin}px`,
        width: `${window.innerWidth - this.margin * 2}px`,
        height: `${window.innerHeight - this.margin * 2}px`,
      };
    }
    return {
      left: `${this.panelX}px`,
      top: `${this.panelY}px`,
      width: `${this.panelWidth}px`,
      height: `${this.panelHeight}px`,
    };
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.clampToViewport();
  }

  onHeaderMouseDown(event: MouseEvent): void {
    if (this.isMaximized || (event.target as HTMLElement).closest('button')) {
      return;
    }
    event.preventDefault();
    this.dragState = {
      startX: event.clientX,
      startY: event.clientY,
      originX: this.panelX,
      originY: this.panelY,
    };
  }

  onResizeMouseDown(edge: ResizeEdge, event: MouseEvent): void {
    if (this.isMinimized || this.isMaximized) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.resizeState = {
      edge,
      startX: event.clientX,
      startY: event.clientY,
      origin: {
        x: this.panelX,
        y: this.panelY,
        width: this.panelWidth,
        height: this.panelHeight,
      },
    };
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (this.dragState) {
      const dx = event.clientX - this.dragState.startX;
      const dy = event.clientY - this.dragState.startY;
      this.panelX = this.dragState.originX + dx;
      this.panelY = this.dragState.originY + dy;
      this.clampToViewport();
      return;
    }

    if (this.resizeState) {
      this.applyResize(event);
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.dragState) {
      this.applyEdgeSnap();
    }
    this.endInteraction();
  }

  minimize(): void {
    if (this.isMinimized) {
      return;
    }
    if (!this.isMaximized) {
      this.savedBounds = {
        x: this.panelX,
        y: this.panelY,
        width: this.panelWidth,
        height: this.panelHeight,
      };
    }
    this.isMinimized = true;
    this.isMaximized = false;
  }

  restore(): void {
    this.isMinimized = false;
    if (this.savedBounds) {
      this.panelX = this.savedBounds.x;
      this.panelY = this.savedBounds.y;
      this.panelWidth = this.savedBounds.width;
      this.panelHeight = this.savedBounds.height;
      this.savedBounds = null;
    }
    this.clampToViewport();
  }

  toggleMaximize(): void {
    if (this.isMaximized) {
      this.isMaximized = false;
      if (this.savedBounds) {
        this.panelX = this.savedBounds.x;
        this.panelY = this.savedBounds.y;
        this.panelWidth = this.savedBounds.width;
        this.panelHeight = this.savedBounds.height;
        this.savedBounds = null;
      }
      this.clampToViewport();
      return;
    }

    this.savedBounds = {
      x: this.panelX,
      y: this.panelY,
      width: this.panelWidth,
      height: this.panelHeight,
    };
    this.isMaximized = true;
    this.isMinimized = false;
  }

  close(): void {
    this.closed.emit();
  }

  download(): void {
    const anchor = document.createElement('a');
    anchor.href = encodeFilePreviewUrl(this.previewUrl);
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = this.fileName || 'download';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private resetDefaultBounds(): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    this.panelWidth = Math.round(viewportWidth * 0.42);
    this.panelHeight = Math.round(viewportHeight * 0.8);
    this.panelX = Math.round(viewportWidth - this.panelWidth - this.margin * 2);
    this.panelY = Math.round(viewportHeight * 0.08);
    this.clampToViewport();
  }

  private clampToViewport(): void {
    const maxWidth = window.innerWidth - this.margin * 2;
    const maxHeight = window.innerHeight - this.margin * 2;
    this.panelWidth = Math.min(Math.max(this.panelWidth, this.minWidth), maxWidth);
    this.panelHeight = Math.min(Math.max(this.panelHeight, this.minHeight), maxHeight);
    this.panelX = Math.min(
      Math.max(this.panelX, this.margin),
      window.innerWidth - this.panelWidth - this.margin,
    );
    this.panelY = Math.min(
      Math.max(this.panelY, this.margin),
      window.innerHeight - this.panelHeight - this.margin,
    );
  }

  private applyEdgeSnap(): void {
    const snapThreshold = 48;
    if (this.panelX <= snapThreshold) {
      this.panelX = this.margin;
    } else if (this.panelX + this.panelWidth >= window.innerWidth - snapThreshold) {
      this.panelX = window.innerWidth - this.panelWidth - this.margin;
    }
  }

  private applyResize(event: MouseEvent): void {
    const state = this.resizeState;
    if (!state) {
      return;
    }

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    const maxWidth = window.innerWidth - this.margin * 2;
    const maxHeight = window.innerHeight - this.margin * 2;
    let { x, y, width, height } = state.origin;

    if (state.edge === 'right' || state.edge === 'corner-br') {
      width = Math.min(Math.max(state.origin.width + dx, this.minWidth), maxWidth);
    }
    if (state.edge === 'left' || state.edge === 'corner-bl') {
      const nextWidth = Math.min(Math.max(state.origin.width - dx, this.minWidth), maxWidth);
      x = state.origin.x + (state.origin.width - nextWidth);
      width = nextWidth;
    }
    if (state.edge === 'bottom' || state.edge === 'corner-br' || state.edge === 'corner-bl') {
      height = Math.min(Math.max(state.origin.height + dy, this.minHeight), maxHeight);
    }

    this.panelX = x;
    this.panelY = y;
    this.panelWidth = width;
    this.panelHeight = height;
    this.clampToViewport();
  }

  private endInteraction(): void {
    this.dragState = null;
    this.resizeState = null;
  }

  private loadPdfPreview(): void {
    const sourceUrl = encodeFilePreviewUrl(this.previewUrl);
    if (!sourceUrl) {
      this.pdfLoadFailed = true;
      return;
    }

    this.isLoadingPdf = true;
    this.pdfLoadFailed = false;

    fetch(sourceUrl, { mode: 'cors' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        this.revokePdfBlobUrl();
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        this.pdfBlobUrl = URL.createObjectURL(pdfBlob);
        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `${this.pdfBlobUrl}#toolbar=1&navpanes=0`,
        );
      })
      .catch(() => {
        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          buildGooglePdfViewerUrl(this.previewUrl),
        );
      })
      .finally(() => {
        this.isLoadingPdf = false;
      });
  }

  private loadJsonPreview(): void {
    this.isLoadingJson = true;
    fetch(encodeFilePreviewUrl(this.previewUrl))
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load JSON preview');
        }
        return response.text();
      })
      .then((text) => {
        try {
          this.jsonPreview = JSON.stringify(JSON.parse(text), null, 2);
        } catch {
          this.jsonPreview = text;
        }
      })
      .catch(() => {
        this.jsonLoadFailed = true;
      })
      .finally(() => {
        this.isLoadingJson = false;
      });
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }
}
